import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ClosingDialog } from './ClosingDialog';
import type { Month } from './types';

/** What closing does, named in the confirmation: the drafts to refuse and the warnings left on record. */
function consequences(month: Month, t: (key: string, options: object) => string): string {
  const drafts = month.draftsToRefuse.length;
  const warnings = month.warnings.length;
  const parts = [
    drafts > 0 ? t('close.drafts', { count: drafts }) : null,
    warnings > 0 ? t('close.warnings', { count: warnings }) : null,
  ].filter((part): part is string => part !== null);
  return parts.length === 0 ? t('close.plain', {}) : `${parts.join(t('close.and', {}))}.`;
}

export function CloseDialog({
  month,
  label,
  busy,
  onConfirm,
  onClose,
}: {
  readonly month: Month;
  readonly label: string;
  readonly busy: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('closing');
  const text = consequences(month, t as (key: string, options: object) => string);
  return (
    <ClosingDialog
      title={t('close.title', { month: label })}
      description={text}
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('close.cancel')}
          </Button>
          <Button type="button" disabled={busy} onClick={onConfirm}>
            {t('close.button')}
          </Button>
        </>
      }
    />
  );
}

export function ReopenDialog({
  label,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  readonly label: string;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onConfirm: (reason: string) => void;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('closing');
  const [reason, setReason] = useState('');
  const empty = reason.trim() === '';
  return (
    <ClosingDialog
      title={t('reopen.title', { month: label })}
      description={t('reopen.help')}
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('reopen.cancel')}
          </Button>
          <Button type="button" disabled={busy || empty} onClick={() => onConfirm(reason)}>
            {t('reopen.confirm')}
          </Button>
        </>
      }
    >
      <label className="flex flex-col gap-1 text-sm font-semibold">
        {t('reopen.reason')}
        <textarea
          value={reason}
          maxLength={2000}
          rows={3}
          onChange={(event) => setReason(event.target.value)}
          className="border-input bg-background min-h-20 rounded-md border px-3 py-2 text-sm font-normal"
        />
      </label>
      {error !== null ? (
        <p role="alert" className="text-destructive text-sm">
          {(t as (key: string) => string)(`errors.${error}`)}
        </p>
      ) : null}
    </ClosingDialog>
  );
}
